import { createContext, useContext, useState, useEffect } from 'react';
import { settingsApi } from '../services/api';

const DEFAULT_BRANDING = {
  primary_color: '#18181B',
  secondary_color: '#059669',
  accent_color: '#7C3AED',
  logo_url: 'https://customer-assets.emergentagent.com/job_fpa-builder/artifacts/rrkna3wt_CGES%20Logo%20Transparent%20Background.png',
  theme: 'light',
  company_name: 'C-Ges FP&A'
};

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [isDark, setIsDark] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedBuId, setSelectedBuId] = useState(null);
  const [buLogos, setBuLogos] = useState({});

  useEffect(() => {
    loadBranding();
  }, []);

  useEffect(() => {
    // Apply theme to document
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const loadBranding = async () => {
    try {
      const response = await settingsApi.getBranding();
      setBranding(response.data);
      setIsDark(response.data.theme === 'dark');
    } catch (error) {
      // Use defaults if not authenticated or error
      setBranding(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  };

  const toggleTheme = () => {
    setIsDark(!isDark);
  };

  const updateBranding = (newBranding) => {
    setBranding(newBranding);
    setIsDark(newBranding.theme === 'dark');
  };

  const setSelectedBu = (buId, logoUrl) => {
    setSelectedBuId(buId);
    if (logoUrl) {
      setBuLogos(prev => ({ ...prev, [buId]: logoUrl }));
    }
  };

  // Get the current logo based on selected BU
  const getCurrentLogo = () => {
    if (selectedBuId && buLogos[selectedBuId]) {
      return buLogos[selectedBuId];
    }
    return branding.logo_url;
  };

  return (
    <ThemeContext.Provider value={{
      branding,
      isDark,
      loading,
      toggleTheme,
      updateBranding,
      setSelectedBu,
      getCurrentLogo,
      selectedBuId
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
